import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { DateFormat, CurrencyFormat } from '../../../helpers/formatting'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { selectDropdowns, selectDropdownOptions } from '../../../redux/dropdownSlice'
import { setChangeMade } from '../../../redux/paymentSchedulePanelSlice'
import { setModal } from '../../../redux/modalSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfo, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import PaymentDatePicker from "../../../components/PaymentDatePicker"
import { format } from 'date-fns';

export default function MakeupPayment(props) {
  const { loanId, refreshPaymentSchedule, navigateSlider, currentDate, customerInfo, paymentMethodHelperText } = props
  const requesting = useRef(false)
  const request = useRequest()
  const dispatch = useDispatch()
  const [responseData, setResponseData] = useState([])
  const [finalResponseData, setFinalResponseData] = useState([])
  const today = new Date().toISOString().split("T")[0];
  const { register, handleSubmit, control, field, setValue, unregister, watch, setError, formState: { errors } } = useForm({ defaultValues: { paymentRows: [{ payment_date: today, payment_amount: 0, payment_method_dropdown_option_id: null }] } });
  const [step, setStep] = useState("step1")
  const [numberOfPayments, setNumberOfPayments] = useState(0)
  const dropdowns = useSelector(selectDropdowns);
  const dropdownOptions = useSelector(selectDropdownOptions)
  const [saving, setSaving] = useState(false)
  const [buttonClicked, setButtonClicked] = useState('');
  const [finalFormData, setFinalFormData] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [amountRemain, setAmountRemain] = useState(0);
  const [fieldNames, setFieldNames] = useState([]);
  const watchedFields = watch(fieldNames);
  const [paymentOptions, setPaymentOptions] = useState([])
  const [selectedDate, setSelectedDate] = useState([]);
  const [paymentMethodDdoIds, setPaymentMethodDdoIds] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const discountReason = dropdowns.data.filter((o) => o.code_key === 'discount_reason')[0];
  const discountReasonOptions = dropdownOptions.filter((o) => o.dropdown_id === discountReason.id && o.optional_value_1 === 'TRUE').map((o) => ({ label: o.name, value: o.id, codeKey: o.code_key }));
  const paymentMethodACHId = dropdownOptions.find((o) => o?.code_key === "payment_method_ach")?.id;
  const paymentMethodDebitCardId = dropdownOptions.find((o) => o?.code_key === "payment_method_debit_card")?.id;

  // all discount
  const [discountType, setDiscountType] = useState("amount")
  const [discountSign, setDiscountSign] = useState("$")
  const [discountInfoText, setDiscountInfoText] = useState("")

  useEffect(() => {
    if (step !== 'step1') {
      dispatch(setChangeMade(true))
    }
  }, [step]);

  useEffect(() => {
    // Calculate the sum whenever any field changes
    const sum = Object.values(watchedFields).reduce((acc, cur) => acc + parseFloat(cur || 0), 0);
    setTotalAmount(sum);
  }, [watchedFields]);

  const { remove, fields, append, update } = useFieldArray({
    name: 'paymentRows',
    control
  })

  const paymentRowsWatch = useWatch({
    name: 'paymentRows',
    control
  })

  useEffect(() => {
    getMakeupInfo(loanId)
    remove();
  }, [])

  const getMakeupInfo = async (loanId) => {
    if (requesting.current) return;
    requesting.current = true;
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/makeup_info`;
      // Check if the currentDate variable has a value and, if so, add it as a query parameter
      if (currentDate !== null) {
        endpoint += `?current_date=${currentDate}`;
      }
      request.setEndpoint(endpoint)
      const response = await request.get()
      const data = response?.data?.data
      setResponseData(data);
      if (data?.max_number_of_payments) {
        setFieldNames(Array.from({ length: data.max_number_of_payments }, (_, i) => `paymentRows.${i}.payment_amount`))
        setPaymentOptions(Array.from({ length: data.max_number_of_payments }, (_, i) => ({
          value: (i + 1).toString(),
          label: (i + 1).toString(),
        })));
      }

      if (!defaultPaymentMethod) {
        const paymentMethod = dropdownOptions.filter((o) => data?.default_payment_method_dropdown_option_id === o.id).map((o) => ({ label: o.name, value: o.id }))[0];
        setDefaultPaymentMethod(paymentMethod);
      }
      setMinDate(currentDate ? currentDate.split(" ")[0] : today)
      requesting.current = false;
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      requesting.current = false;
    }
  }

  function serialize(obj, prefix) {
    const str = [];
    Object.keys(obj).forEach((key) => {
      const k = prefix ? `${prefix}[${key}]` : key;
      const v = obj[key];
      str.push(
        v !== null && typeof v === "object"
          ? serialize(v, k)
          : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
      );
    });
    return str.join("&");
  }

  const onSubmit = async (formData) => {
    const paymentMethodOption = formData.paymentRows[0].payment_method_dropdown_option_id.value;
    if (paymentMethodACHId == paymentMethodOption && customerInfo?.ach_revocation_active) {
      dispatch(
        setModal({
          is_active: true,
          type: 'allow_ach_payments_modal',
          options: { customerId: customerInfo.id, hasBkConfirmed: customerInfo?.has_bk_confirmed }
        })
      )
    } else {
      if (requesting.current) return;
      if (Array.isArray(formData.payment_method_dropdown_option_id)) {
        formData.payment_method_dropdown_option_id = formData.payment_method_dropdown_option_id.map((item) => item.value);
      }

      formData.discount_reason_dropdown_option_id = formData.discount_reason_dropdown_option_id?.value ?? null;
      if ((+formData.principal_discount != 0 || +formData.interest_discount != 0 || +formData.late_fee_discount != 0 || +formData.nsf_fee_discount != 0) && formData.discount_reason_dropdown_option_id == null) {
        setError("discount_reason_dropdown_option_id", { type: "manual", message: "Please select a reason for the discount" });
        return
      }

      if (formData.discount_reason_dropdown_option_id == null) {
        delete formData.discount_reason_dropdown_option_id;
      }

      setSaving(true)
      let rowQueryShape = {
        preview: {},
        submit: {
          payment_date: [],
          payment_amount: [],
          payment_method_dropdown_option_id: []
        }
      }

      // change the paramater name to match the API if percent selected
      if (discountType === "percent") {
        formData.principal_discount = responseData?.unscheduled_principal_amount * (formData.principal_discount / 100);
        formData.interest_discount = responseData?.unscheduled_interest_amount * (formData.interest_discount / 100);
        formData.late_fee_discount = responseData?.unscheduled_late_fee * (formData.late_fee_discount / 100);
        formData.nsf_fee_discount = responseData?.unscheduled_nsf_fee * (formData.nsf_fee_discount / 100);
      }

      if (buttonClicked == "preview") {
        formData?.paymentRows?.forEach((row, index) => {
          rowQueryShape.preview[`payment_date[${index}]`] = row.payment_date;
          rowQueryShape.preview[`payment_amount[${index}]`] = row.payment_amount;
          rowQueryShape.preview[`payment_method_dropdown_option_id[${index}]`] = row.payment_method_dropdown_option_id.value;
          rowQueryShape.submit.payment_date.push(row.payment_date);
          rowQueryShape.submit.payment_amount.push(row.payment_amount);
          rowQueryShape.submit.payment_method_dropdown_option_id.push(row.payment_method_dropdown_option_id.value);
        })
        let formDataCopy = {
          ...formData,
        }
        delete formDataCopy.paymentRows;
        const queryString = serialize({
          ...formDataCopy,
          ...rowQueryShape.preview
        });
        try {
          let endpoint = `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/makeup_preview?${queryString}`;
          if (currentDate !== null) {
            endpoint += `&current_date=${currentDate}`;
          }
          request.setEndpoint(endpoint)

          setFinalFormData({ ...formDataCopy, ...rowQueryShape.submit })
          const response = await request.get(formData)
          if (response.status === 200 || response.status === 201) {
            setSaving(false)
            setFinalResponseData(response?.data?.data)
            setStep("step3")
          } else {
            setSaving(false)
            dispatch(setNotification({ type: 'error', body: `Error: ${response.response?.data?.message}` }))
          }
        } catch (error) {
          setSaving(false)
          const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
          dispatch(setNotification({ type: 'error', body: errorMessage }))
        }
      } else if (buttonClicked == "schedule") {
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment_schedule/makeup`)
        try {
          if (currentDate !== null) {
            finalFormData['current_date'] = currentDate;
          }
          const response = await request.post(finalFormData)
          if (response.status === 200 || response.status === 201) {
            setSaving(false)
            dispatch(setNotification({ type: 'success', body: '' }))
            navigateSlider("form_submit")
            refreshPaymentSchedule()
          } else {
            setSaving(false)
            dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
          }
        } catch (error) {
          setSaving(false)
          const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
          dispatch(setNotification({ type: 'error', body: errorMessage }))
        }
      }
    }
  };

  /**
   * useEffect hook to calculate and set payment amounts and dates.
   *
   * This hook is triggered whenever `numberOfPayments` or `setValue` change.
   * It calculates the total payment amount and the individual payment amounts
   * based on the provided `numberOfPayments` and `responseData.unscheduled_total`.
   * It then iterates through the number of payments, setting the payment amount
   * and payment date for each payment. If it's the last payment, it sets the
   * payment amount to the remainder to account for rounding errors.
   *
   * @listens numberOfPayments - The number of payments to be made.
   * @listens setValue - Function to set the payment amount and payment date.
   */
  useEffect(() => {
    if (numberOfPayments > 0) {
      const total = parseFloat(responseData?.unscheduled_total || 0).toFixed(2);
      const avgWithFraction = total / numberOfPayments;
      const avgWithDecimal = (total / numberOfPayments).toFixed(2);
      let pAmount = avgWithDecimal;
      if (avgWithFraction > avgWithDecimal) {
        pAmount = (+avgWithDecimal + +0.01).toFixed(2);
      }
      const totalPayments = pAmount * (numberOfPayments - 1).toFixed(2);
      const remainder = (total - totalPayments).toFixed(2);

      remove();

      for (let i = 0; i < numberOfPayments; i++) {
        const value = i === (numberOfPayments - 1) ? remainder : pAmount;

        const dateBuilder = () => {
          if (selectedDate[i]) {
            return format(selectedDate[i], 'yyyy-MM-dd')
          } else {
            return currentDate ? currentDate.split(" ")[0] : today
          }
        }

        const paymentMethodBuilder = () => {
          return dropdownOptions.filter((o) => (paymentMethodDdoIds[i] ?? responseData?.default_payment_method_dropdown_option_id) == o.id).map((o) => ({
            label: o.name,
            value: o.id
          }))[0]
        }

        let rowAppendData = {
          payment_date: dateBuilder(),
          payment_amount: value,
          payment_method_dropdown_option_id: paymentMethodBuilder()
        }
        append(rowAppendData)
      }
    }
  }, [numberOfPayments, setValue]);

  const checkChangesMade = () => {
    navigateSlider("");
  }

  /**
   * useEffect hook to update the remaining amount.
   *
   * This hook is triggered whenever `totalAmount` changes. It calculates the
   * remaining amount by subtracting `totalAmount` from `unscheduled_total`
   * in the `responseData`. Both values are rounded to two decimal places
   * before subtraction.
   *
   * @listens totalAmount - The total amount spent so far.
   */
  useEffect(() => {
    if (responseData?.unscheduled_total !== undefined) {
      setAmountRemain(
        (parseFloat(responseData?.unscheduled_total).toFixed(2) - parseFloat(totalAmount).toFixed(2))
      );
    }
  }, [totalAmount]);

  useEffect(() => {
    if (discountType === "amount") {
      setDiscountSign("$")
      setDiscountInfoText("Discounting by amount selected")
    } else if (discountType === "percent") {
      setDiscountSign("%")
      setDiscountInfoText("Discounting by percentage selected")
    }
  }, [discountType])

  return (
    <div className="pl-5 py-3">
      {responseData.length == 0 && <div className="is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }}>--- Loading ---</div>}

      {responseData.length != 0 && (
        <form onSubmit={handleSubmit(onSubmit)} id="makeup_payment_form">
          {(step === "step1" || step === "step2" || step === "step3") && (
            <>
              <p className="title is-5">Makeup Payment</p>
              <table className="table is-narrow table-payment-schedule-panel">
                <tbody>
                {(!!responseData?.has_winback_promo && amountRemain.toFixed(2) <= 0) && (
                  <tr>
                    <td colSpan={4}>
                      <strong>Promotion:</strong> Winback 50%,
                    </td>
                  </tr>
                )}

                <tr>
                  <td colSpan={4}>
                    <strong>Bank Name:</strong> {customerInfo?.bank_name}, <strong>Account
                    Number:</strong> ###########{customerInfo?.bank_account_number_last_four}
                    {customerInfo?.debit_card_number_last_four ? (
                      <><br /><strong>Debit Card
                        Number:</strong> ###########{customerInfo?.debit_card_number_last_four}</>
                    ) : null}
                  </td>
                </tr>
                <tr>
                  <td><b>Outstanding Balance</b></td>
                  <td><CurrencyFormat value={responseData?.unscheduled_total} /></td>
                  <td colSpan={2}></td>
                </tr>
                <tr>
                  <td><b>Number of Payments</b></td>
                  <td>
                    <Select
                      options={paymentOptions}
                      classNames={reactSelectClassNames()}
                      menuPosition="fixed"
                      onChange={(e) => {
                        setStep('step2')
                        setNumberOfPayments(e.value)
                      }}
                      isSearchable={false}
                      styles={{ container: (base) => ({ ...base, minWidth: '100px' }) }}
                    />
                  </td>
                  <td colSpan={2} className="py-2">
                    <div className="has-text-right">
                      <button
                        type="button"
                        className="button has-background-grey has-text-white is-small mr-4"
                        onClick={() => checkChangesMade()}
                      >
                        <span className="mx-3">Cancel</span>
                      </button>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </>
          )}

          {(step === 'step2' || step == 'step3') && (
            <>
              <table className="table is-narrow table-payment-schedule-panel">
                <thead>
                <tr>
                  <th className="pr-2 py-2">Payment Details</th>
                  <th className="pr-2 py-2">Date</th>
                  <th className="pr-2 py-2" style={{ minWidth: '150px' }}>Scheduled Amount</th>
                  <th>
                  Payment Method <span
                    className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                    data-tooltip={paymentMethodHelperText}
                  >
                      <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                    </span>
                  </th>
                  <th colSpan={2}></th>
                </tr>
              </thead>

                <tbody>
                {fields.map((field, index) => {
                  return (
                    <tr key={field.id}>
                      <td></td>
                      <td className="pr-2 py-2">
                        <Controller
                          name={`paymentRows.${index}.payment_date`}
                          control={control}
                          render={({ field }) => (
                            <PaymentDatePicker
                              paymentMethodDdoId={paymentMethodDdoIds[`${index}`] ? paymentMethodDdoIds[`${index}`] : defaultPaymentMethod.value}
                              max={`${responseData?.max_makeup_date}T00:00:00`}
                              min={`${minDate}T00:00:00`}
                              selectedDate={selectedDate[`${index}`] ?? new Date(minDate + 'T00:00:00')}
                              setSelectedDate={(date) => {
                                field.onChange(date ? format(date, 'yyyy-MM-dd') : minDate)
                                setSelectedDate((selectedDate) => {
                                  const copy = [...selectedDate]
                                  copy[index] = date
                                  return copy
                                })
                              }}
                            />
                          )}
                        />
                      </td>
                      <td className="pr-2 py-2">
                        <input
                          className="input is-small"
                          type="number"
                          step="0.01"
                          {...register(`paymentRows.${index}.payment_amount`)}
                        />
                      </td>
                      <td>
                        <Controller
                          name={`paymentRows.${index}.payment_method_dropdown_option_id`}
                          defaultValue={dropdownOptions.filter((o) => responseData?.default_payment_method_dropdown_option_id == o.id).map((o) => ({
                            label: o.name,
                            value: o.id,
                          }))[0]}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              {...register(`paymentRows.${index}.payment_method_dropdown_option_id`)}
                              options={dropdownOptions.filter((o) => responseData?.payment_method_dropdown_option_ids.includes(o.id)).map((o) => ({
                                label: o.name,
                                value: o.id,
                              }))}
                              isSearchable={false}
                              classNames={reactSelectClassNames()}
                              onChange={(val) => {
                                field.onChange({ label: val?.label, value: val?.value })
                                setPaymentMethodDdoIds((paymentMethodDdoIds) => {
                                  const copy = [...paymentMethodDdoIds]
                                  copy[index] = val?.value
                                  return copy
                                })
                              }}
                              menuPosition="fixed"
                              form="makeup_payment_form"
                              styles={{
                                container: (base) => ({ ...base, minWidth: '200px' }),
                                menu: ({ ...css }) => ({ ...css, width: 'max-content' }),
                              }}
                            />
                          )}
                        />
                      </td>
                      <td className="pr-2 py-2">{index === numberOfPayments - 1 && (
                        <>Date can&apos;t go past <DateFormat
                          dateString={responseData?.max_makeup_date}
                        /></>
                      )}</td>
                      <td></td>
                    </tr>
                  )
                })}

                <tr>
                  <td colSpan={3} className="has-text-right">
                    {amountRemain.toFixed(2) > 0 ? (
                      <b className="has-text-danger">There will still be an outstanding balance of: <CurrencyFormat value={amountRemain.toFixed(2)} /></b>
                    ) : (
                      <b>Remaining Balance: <CurrencyFormat value={amountRemain.toFixed(2)} /></b>
                    )}
                  </td>
                  <td className="pr-2 py-2"></td>
                  <td className="pr-2 py-2"></td>
                </tr>
                {!!responseData?.has_winback_promo && responseData?.next_regular_payment?.scheduled_date && responseData?.next_regular_payment_total_discount > 0 && (
                  <tr>
                    <td colSpan={5} className="has-text-center word-wrap-break-word">
                      {amountRemain.toFixed(2) <= 0 ? (
                        <i>Upon successfully submitting this Makeup Payment, the Customer’s Next scheduled payment on <DateFormat dateString={responseData?.next_regular_payment?.scheduled_date} /> will be
                          discounted <CurrencyFormat value={responseData?.next_regular_payment_total_discount} /> as a result of the Winback
                          Promotion.</i>
                      ) : (
                        <b></b>
                      )}
                    </td>
                  </tr>
                )}

                </tbody>
              </table>

              <table className="table is-narrow table-payment-schedule-panel">
                <thead>
                <tr>
                  <td className="pt-5" colSpan={5}>
                    <b>Discount Type</b>: <span className="has-text-primary">{discountInfoText}</span>
                    <div>
                      <div className="control">
                        <label className="radio mr-3">
                          <input
                            name="discount_type"
                            value="amount"
                            type="radio"
                            onChange={() => setDiscountType('amount')}
                            checked={discountType === "amount"}
                          /> Amount ($)
                          </label> OR <label className="radio ml-3">
                            <input name="discount_type" value="percent" type="radio" onChange={() => setDiscountType("percent")} checked={discountType === "percent"} /> Percent (%)
                          </label>
                        </div>
                      </div>
                      <p></p>
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr>
                    <th className="pr-2 py-2">Payment Discounts</th>
                    <th className="pr-2 py-2" style={{ width: '250px' }}>Principal {discountSign}</th>
                    <th className="pr-2 py-2" style={{ width: '250px' }}>Interest {discountSign}</th>
                    <th className="pr-2 py-2" style={{ width: '250px' }}>Late Fee {discountSign}</th>
                    <th className="pr-2 py-2" style={{ width: '250px' }}>NSF Fee {discountSign}</th>
                    <td colSpan={2}></td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td></td>
                    <td className="pr-2 py-2">
                      <Controller
                        name={`principal_discount`}
                        control={control}
                        defaultValue={0}
                        render={({ field }) => (
                          <input
                            {...field}
                            {...register(`principal_discount`)}
                            name={`principal_discount`}
                            className="input is-small"
                            style={{ width: '125px' }}
                            type="number"
                            step="0.01"
                            form="makeup_payment_form"
                            max={responseData?.unscheduled_principal}
                          />
                        )}
                      />
                    </td>
                    <td className="pr-2 py-2">
                      <Controller
                        name={`interest_discount`}
                        control={control}
                        defaultValue={0}
                        render={({ field }) => (
                          <input
                            {...field}
                            {...register(`interest_discount`)}
                            name={`interest_discount`}
                            className="input is-small"
                            style={{ width: '125px' }}
                            type="number"
                            step="0.01"
                            form="makeup_payment_form"
                            max={responseData?.unscheduled_interest}
                          />
                        )}
                      />
                    </td>
                    <td className="pr-2 py-2">
                      <Controller
                        name={`late_fee_discount`}
                        control={control}
                        defaultValue={0}
                        render={({ field }) => (
                          <input
                            {...field}
                            {...register(`late_fee_discount`)}
                            name={`late_fee_discount`}
                            className="input is-small"
                            style={{ width: '125px' }}
                            type="number"
                            step="0.01"
                            form="makeup_payment_form"
                            max={responseData?.unscheduled_late_fee}
                          />
                        )}
                      />
                    </td>
                    <td className="pr-2 py-2">
                      <Controller
                        name={`nsf_fee_discount`}
                        control={control}
                        defaultValue={0}
                        render={({ field }) => (
                          <input
                            {...field}
                            {...register(`nsf_fee_discount`)}
                            name={`nsf_fee_discount`}
                            className="input is-small"
                            style={{ width: '125px' }}
                            type="number"
                            step="0.01"
                            form="makeup_payment_form"
                            max={responseData?.unscheduled_nsf_fee}
                          />
                        )}
                      />
                    </td>
                    <div className="has-text-right">
                      <button
                        type="button"
                        className="button has-background-grey has-text-white is-small mr-1"
                        onClick={() => checkChangesMade()}
                      >
                        <span className="mx-3">Cancel</span>
                      </button>
                      <button
                        type="submit"
                        className="button is-link is-small mr-4"
                        name="preview"
                        form="makeup_payment_form"
                        onClick={() => setButtonClicked('preview')}
                        disabled={saving || (amountRemain < 0)}
                      >
                        <span className="mx-3">Preview</span>
                      </button>
                    </div>
                  </tr>
                  <tr>
                    <td>
                      Discount Reason
                    </td>
                    <td colSpan={4}>
                      <Controller
                        name="discount_reason_dropdown_option_id"
                        control={control}
                        render={({ field }) => {
                          return (
                            <Select
                              {...field}
                              options={discountReasonOptions}
                              isSearchable={false}
                              classNames={reactSelectClassNames(errors, 'discount_reason_dropdown_option_id')}
                              menuPosition="fixed"
                              styles={{ container: (base) => ({ ...base, width: '100%' }), menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                              form="makeup_payment_form"
                              isClearable
                            />
                          )
                        }}
                      />
                      {errors?.discount_reason_dropdown_option_id?.message && <p className="has-text-danger">{errors?.discount_reason_dropdown_option_id?.message}</p>}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {step === 'step3' && (
            <>
              <div className="divider m-0 mx-1 mb-3"></div>
              <table className="table is-striped is-fullwidth">
                <tbody>
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Type</th>
                    <th>Principal</th>
                    <th>Interest</th>
                    <th>Loan Fee</th>
                    <th>Late Fee</th>
                    <th>NSF Fee</th>
                    <th>Total</th>
                    <th>Status</th>
                  </tr>
                  {finalResponseData.map((row, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                      <td><DateFormat dateString={row.scheduled_date} /></td>
                      <td>{dropdownOptions.filter((o) => o.id === row.payment_method_dropdown_option_id)[0].name}</td>
                      <td>{dropdownOptions.filter((o) => o.id === row.payment_type_dropdown_option_id)[0].name}</td>
                      <td><CurrencyFormat value={row.principal_amount} />
                        {(row.prediscount_principal != row.principal_amount) && (
                          <span
                            className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                            data-tooltip={`Principal Amount: $${row.principal_amount}\nPre-Discount Principal: $${row.prediscount_principal}`}
                          >
                            <FontAwesomeIcon
                              icon={faInfo}
                              className="pl-1 has-text-link"
                              style={{ paddingBottom: '.15rem !important' }}
                            />
                          </span>
                        )}</td>
                      <td><CurrencyFormat value={row.interest_amount} />
                        {(row.prediscount_interest != row.interest_amount) && (
                          <span
                            className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                            data-tooltip={`Interest Amount: $${row.interest_amount}\nPre-Discount Interest: $${row.prediscount_interest}`}
                          >
                            <FontAwesomeIcon
                              icon={faInfo}
                              className="pl-1 has-text-link"
                              style={{ paddingBottom: '.15rem !important' }}
                            />
                          </span>
                        )}</td>
                      <td><CurrencyFormat value={row.loan_fee} /></td>
                      <td><CurrencyFormat value={row.late_fee} />
                        {(row.prediscount_late_fee != row.late_fee) && (
                          <span
                            className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                            data-tooltip={`Late Fee Amount: $${row.late_fee}\nPre-Discount Late Fee: $${row.prediscount_late_fee}`}
                          >
                            <FontAwesomeIcon
                              icon={faInfo}
                              className="pl-1 has-text-link"
                              style={{ paddingBottom: '.15rem !important' }}
                            />
                          </span>
                        )}</td>
                      <td><CurrencyFormat value={row.nsf_fee} />
                        {(row.prediscount_nsf_fee != row.nsf_fee) && (
                          <span
                            className={'lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-weight-normal'}
                            data-tooltip={`NSF Fee Amount: $${row.nsf_fee}\nPre-Discount NSF Fee: $${row.prediscount_nsf_fee}`}
                          >
                            <FontAwesomeIcon
                              icon={faInfo}
                              className="pl-1 has-text-link"
                              style={{ paddingBottom: '.15rem !important' }}
                            />
                          </span>
                        )}</td>
                      <td><CurrencyFormat value={row.scheduled_amount} /></td>
                      <td>{dropdownOptions.filter((o) => o.id === row.payment_status_dropdown_option_id)[0].name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="has-text-right">
                <button
                  type="button"
                  className="button has-background-grey has-text-white is-small mr-1"
                  onClick={() => checkChangesMade()}
                >
                  <span className="mx-3">Cancel</span>
                </button>
                <button
                  type="submit"
                  className="button is-link is-small mr-4"
                  name="schedule"
                  form="makeup_payment_form"
                  onClick={() => setButtonClicked('schedule')}
                  disabled={saving && 'disabled'}
                >
                  <span className="mx-3">Schedule</span>
                </button>
              </div>
            </>
          )}
        </form>
      )}
    </div>
  )
}
